import MicroModal from "micromodal";
import AccessibilityUtilities from "../utilities/accessibility";

export class Modal {
  private element: HTMLElement;
  private modalId: string;
  private triggers: NodeListOf<HTMLAnchorElement>;
  private micromodalOptions: Object;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.modalId = this.element.id;
      this.triggers = document.querySelectorAll(
        `[data-micromodal-trigger="${this.modalId}"]`
      );
      this.micromodalOptions = {
        onShow: () => {
          document.body.style.overflow = "hidden";
        },
        onClose: () => {
          document.body.style.removeProperty("overflow");
          const modalIFrame = this.element.querySelector(
            "iframe"
          ) as HTMLIFrameElement;
          if (modalIFrame) {
            modalIFrame.src = modalIFrame.src;
          }
        },
        openClass: "is-open",
        disableScroll: false,
        awaitOpenAnimation: window.matchMedia("(prefers-reduced-motion)")
          .matches
          ? false
          : true,
        awaitCloseAnimation: window.matchMedia("(prefers-reduced-motion)")
          .matches
          ? false
          : true,
        disableFocus: true,
      };
      this.init();
    }
  }

  private init() {
    this.convertAnchorsToButtons();
    this.initializeModals();
    this.moveModalToBottomOfBody();
    this.disableInnerLinkTabbing();
    this.mutationObserveAriaHidden();
    this.openIfURLParameters();
  }

  private convertAnchorsToButtons() {
    const closers = this.element.querySelectorAll(
      "a[data-micromodal-close]"
    ) as NodeListOf<HTMLAnchorElement>;
    const anchors = [
      ...Array.from(this.triggers),
      ...Array.from(closers),
    ] as HTMLAnchorElement[];

    for (let i = 0; i < anchors.length; i++) {
      AccessibilityUtilities.convertAnchorToButton(anchors[i]);
    }
  }

  private initializeModals() {
    MicroModal.init(this.micromodalOptions);
  }

  private moveModalToBottomOfBody() {
    document.body.appendChild(this.element);
  }

  private appendURLParameter() {
    const url = new URL(window.location.href);
    const currentModalId = this.element.id;
    const existingModal = url.searchParams.get("content");

    if (!existingModal) {
      url.searchParams.append("content", currentModalId);
      window.history.replaceState(null, "", url.toString());
    }
  }

  private removeURLParameter() {
    const url = new URL(window.location.href);
    url.searchParams.delete("content");
    window.history.replaceState(null, "", url.toString());
  }

  private openIfURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has("content")) {
      const modalId = urlParams.get("content");
      if (modalId) {
        MicroModal.show(modalId, this.micromodalOptions);
      }
    }
  }

  private disableInnerLinkTabbing() {
    const focusableElements = this.element.querySelectorAll(
      "a[href]:not(.skip-link), button, input, textarea, select, form, details, audio, video, iframe, table"
    ) as NodeListOf<HTMLElement>;
    focusableElements.forEach((focusableElement) => {
      focusableElement.setAttribute("tabindex", "-1");
    });
  }

  private mutationObserveAriaHidden() {
    // using a mutation observer to detect when aria hidden changes to update tablinks within
    var elemToObserve = this.element;
    var prevAriaHiddenState = elemToObserve.getAttribute("aria-hidden");

    var observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "aria-hidden") {
          var targetElement = mutation.target as Element;
          var currentAriaHiddenState =
            targetElement.getAttribute("aria-hidden");
          if (prevAriaHiddenState !== currentAriaHiddenState) {
            prevAriaHiddenState = currentAriaHiddenState;
            if (currentAriaHiddenState === "true") {
              this.removeURLParameter();
            } else {
              this.appendURLParameter();
            }
          }
        }
      });
    });

    observer.observe(elemToObserve, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
  }
}

export default function modalInit() {
  const modals = document.querySelectorAll(
    ".modal__lightbox"
  ) as NodeListOf<HTMLElement>;
  for (let i = 0; i < modals.length; i++) {
    new Modal(modals[i]);
  }
}
